import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSecretsApi, SecretMetadata } from '@/api/useSecretsApi';
import { SecretsList } from './SecretsList';
import { CreateSecretDialog } from './CreateSecretDialog';
import { useOrganization } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

type Tab = 'graph' | 'user' | 'org';

interface Props {
    graphId?: string;
    onClose: () => void;
}

export const SecretsPanel: React.FC<Props> = ({ graphId, onClose }) => {
    const { organization } = useOrganization();
    const showOrgTab = !!organization;

    const tabs: Tab[] = [...(graphId ? (['graph'] as Tab[]) : []), 'user' as Tab, ...(showOrgTab ? (['org'] as Tab[]) : [])];

    const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSecret, setEditingSecret] = useState<SecretMetadata | null>(null);

    const api = useSecretsApi();
    const queryClient = useQueryClient();

    const queryKey = activeTab === 'graph' ? ['secrets', 'graph', graphId] : ['secrets', activeTab];

    const { data: secrets = [], isLoading } = useQuery({
        queryKey,
        queryFn: () => {
            switch (activeTab) {
                case 'graph':
                    return graphId ? api.listGraphSecrets(graphId) : Promise.resolve([]);
                case 'user':
                    return api.listUserSecrets();
                case 'org':
                    return api.listOrgSecrets();
            }
        },
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey });
        // Also invalidate the available secrets query used by SecretPortEditor
        queryClient.invalidateQueries({ queryKey: ['availableSecrets'] });
    };

    const handleCreate = async (data: { name: string; value?: string; description?: string; service?: string }) => {
        if (!data.value) return;
        const createData = { name: data.name, value: data.value, description: data.description, service: data.service };
        switch (activeTab) {
            case 'graph':
                if (graphId) await api.createGraphSecret(graphId, createData);
                break;
            case 'user':
                await api.createUserSecret(createData);
                break;
            case 'org':
                await api.createOrgSecret(createData);
                break;
        }
        invalidate();
    };

    const handleUpdate = async (data: { name: string; value?: string; description?: string; service?: string }) => {
        if (!editingSecret) return;
        const updateData = { value: data.value, description: data.description, service: data.service };
        switch (activeTab) {
            case 'graph':
                if (graphId) await api.updateGraphSecret(graphId, editingSecret.id, updateData);
                break;
            case 'user':
                await api.updateUserSecret(editingSecret.id, updateData);
                break;
            case 'org':
                await api.updateOrgSecret(editingSecret.id, updateData);
                break;
        }
        setEditingSecret(null);
        invalidate();
    };

    const handleDelete = async (secret: SecretMetadata) => {
        switch (activeTab) {
            case 'graph':
                if (graphId) await api.deleteGraphSecret(graphId, secret.id);
                break;
            case 'user':
                await api.deleteUserSecret(secret.id);
                break;
            case 'org':
                await api.deleteOrgSecret(secret.id);
                break;
        }
        invalidate();
    };

    const tabLabels: Record<Tab, string> = {
        graph: 'Workflow',
        user: 'Personal',
        org: organization?.name ?? 'Org',
    };

    const emptyMessages: Record<Tab, string> = {
        graph: 'No workflow secrets yet.',
        user: 'No personal secrets yet.',
        org: 'No organization secrets yet.',
    };

    return (
        <div
            className={cn(
                'absolute top-4 left-4 w-[380px] h-[70%]',
                'flex flex-col z-[1000] pointer-events-auto overflow-hidden',
                'rounded-2xl border shadow-xl',
                'bg-white/80 dark:bg-neutral-900/80',
                'backdrop-blur-xl',
                'border-white/30 dark:border-neutral-700/50',
                'animate-in slide-in-from-left-5 fade-in duration-200',
            )}
        >
            {/* Header */}
            <div
                className={cn(
                    'flex items-center justify-between px-4 py-3.5',
                    'border-b rounded-t-2xl',
                    'bg-white/50 dark:bg-neutral-800/50',
                    'backdrop-blur-sm',
                    'border-white/20 dark:border-neutral-700/30',
                )}
            >
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Secrets</h2>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className={cn(
                        'p-1 rounded-md transition-colors',
                        'text-neutral-500 dark:text-neutral-400',
                        'hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
                        'hover:text-neutral-700 dark:hover:text-neutral-200',
                    )}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-3 pt-2.5 pb-1">
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={cn(
                            'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                            activeTab === tab
                                ? 'bg-neutral-200/80 dark:bg-neutral-700/80 text-neutral-900 dark:text-neutral-100'
                                : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300',
                        )}
                    >
                        {tabLabels[tab]}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-2 py-2">
                {isLoading ? (
                    <p className="text-sm text-neutral-500 text-center py-6">Loading...</p>
                ) : (
                    <SecretsList
                        secrets={secrets}
                        onEdit={secret => {
                            setEditingSecret(secret);
                            setDialogOpen(true);
                        }}
                        onDelete={handleDelete}
                        emptyMessage={emptyMessages[activeTab]}
                    />
                )}
            </div>

            {/* Footer */}
            <div className="px-3 py-2.5 border-t border-white/20 dark:border-neutral-700/30">
                <Button
                    size="sm"
                    className="w-full gap-1.5"
                    onClick={() => {
                        setEditingSecret(null);
                        setDialogOpen(true);
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add Secret
                </Button>
            </div>

            {/* Dialog */}
            <CreateSecretDialog
                key={dialogOpen ? (editingSecret?.id ?? 'create') : 'closed'}
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingSecret(null);
                }}
                onSubmit={editingSecret ? handleUpdate : handleCreate}
                onGenerate={api.generateSecret}
                scopeLabel={tabLabels[activeTab]}
                editMode={
                    editingSecret
                        ? {
                              name: editingSecret.name,
                              description: editingSecret.description ?? undefined,
                              service: editingSecret.service ?? undefined,
                          }
                        : undefined
                }
            />
        </div>
    );
};
